import React, { useEffect, useState } from 'react';
import { Button, Drawer, Form, Select, message, Typography } from 'antd';
import { useAppContext } from '@/components/AppContext';
import { CouponSegmentsData, CreateCouponSegment } from '@a4b/api/src/modules/Monetisation/Offerings/types';

interface Props {
    campaignId: string;
    showDrawer: boolean;
    closeDrawer: () => void;
}

const { Option } = Select;
const { Title, Text } = Typography;

const ManageCouponSegments: React.FC<Props> = ({
    campaignId,
    showDrawer,
    closeDrawer
}) => {
    const { networkInstance } = useAppContext();
    const { monetisationOfferingsApi } = networkInstance.clientWithHeaders;
    const [messageApi, contextHolder] = message.useMessage();
    const [couponSegments, setCouponSegments] = useState<CouponSegmentsData>();
    const [selectedSegments, setSelectedSegments] = useState<string[]>([]);
    const [existingSegments, setExistingSegments] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const getCouponSegments = async () => {
        if (!campaignId) return;

        try {
            setIsLoading(true);
            const response = await monetisationOfferingsApi.couponSegmentsApi.getCampaignSegments(campaignId);
            setCouponSegments(response?.data?.data);
            if (response?.data?.data?.segments) {
                const currentSegmentCodes = response.data.data.segments.map(segment => segment.segment_code);
                setSelectedSegments(currentSegmentCodes);
                setExistingSegments(currentSegmentCodes);
            }
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            console.log("Error fetching coupon segments: ", error);
            messageApi.open({
                type: 'error',
                content: 'Failed to fetch coupon segments',
            });
        }
    };

    const saveCouponSegments = async () => {
        if (!campaignId) return;

        const newSegments = selectedSegments.filter(segment => !existingSegments.includes(segment));

        if (newSegments.length === 0) {
            messageApi.open({
                type: 'info',
                content: 'No new segments to add',
            });
            return;
        }

        try {
            setIsSaving(true);
            const payload: CreateCouponSegment = {
                campaign_id: campaignId,
                segment_ids: newSegments
            };

            await monetisationOfferingsApi.couponSegmentsApi.createCampaignSegment(payload);
            messageApi.open({
                type: 'success',
                content: `Added ${newSegments.length} new segment(s) successfully`,
            });

            // Update existing segments to include newly added ones
            setExistingSegments([...existingSegments, ...newSegments]);

            // Refresh the segments list to get updated data with IDs
            getCouponSegments();
            setIsSaving(false);
        } catch (error) {
            setIsSaving(false);
            console.log("Error updating coupon segments: ", error);
            messageApi.open({
                type: 'error',
                content: 'Failed to update coupon segments',
            });
        }
    };

    const handleSegmentChange = async (value: string[]) => {
        const removedSegments = existingSegments.filter(segment => !value.includes(segment));

        if (removedSegments.length > 0) {
            const segmentsToDelete = couponSegments?.segments?.filter(
                segment => removedSegments.includes(segment.segment_code)
            );

            if (segmentsToDelete && segmentsToDelete.length > 0) {
                try {
                    const deletePromises = segmentsToDelete.map(segment =>
                        monetisationOfferingsApi.couponSegmentsApi.deleteCouponSegment(segment.id)
                    );

                    await Promise.all(deletePromises);

                    messageApi.open({
                        type: 'success',
                        content: `Removed ${removedSegments.length} segment(s) successfully`,
                    });

                    setExistingSegments(existingSegments.filter(segment => !removedSegments.includes(segment)));

                } catch (error) {
                    console.log("Error deleting coupon segments: ", error);
                    messageApi.open({
                        type: 'error',
                        content: 'Failed to delete segments',
                    });
                    return;
                }
            }
        }

        setSelectedSegments(value);
    };

    useEffect(() => {
        if (showDrawer && campaignId) {
            getCouponSegments();
        }
    }, [showDrawer, campaignId]);

    return (
        <>
            {contextHolder}
            <Drawer
                title={`Manage Campaign Segments`}
                placement='right'
                onClose={closeDrawer}
                visible={showDrawer}
                destroyOnClose={true}
                size='default'
                width={1000}
                footer={
                    <div style={{ textAlign: 'right' }}>
                        <Button onClick={closeDrawer} style={{ marginRight: 8 }}>
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            onClick={saveCouponSegments}
                            loading={isSaving}
                            disabled={isLoading || selectedSegments.filter(segment => !existingSegments.includes(segment)).length === 0}
                        >
                            {selectedSegments.filter(segment => !existingSegments.includes(segment)).length > 0
                                ? `Add ${selectedSegments.filter(segment => !existingSegments.includes(segment)).length} New Segment(s)`
                                : 'No New Segments to Add'
                            }
                        </Button>
                    </div>
                }
            >
                {isLoading ? (
                    <div style={{ textAlign: 'center', padding: '50px 0' }}>
                        <Text>Loading segments...</Text>
                    </div>
                ) : (
                    <div>
                        <Form layout="vertical">
                            <Form.Item>
                                <Select
                                    mode="tags"
                                    allowClear
                                    style={{ width: '100%' }}
                                    placeholder="Type segment code and press Enter to add, or select existing ones"
                                    value={selectedSegments}
                                    onChange={handleSegmentChange}
                                    optionLabelProp="label"
                                    size="large"
                                    tokenSeparators={[',']}
                                >
                                    {couponSegments?.segments?.map((segment) => (
                                        <Option key={segment.segment_code} value={segment.segment_code} label={segment.segment_code}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <span>{segment.segment_code}</span>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    {segment.id}
                                                </Text>
                                            </div>
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Form>

                        {selectedSegments.length > 0 && (
                            <div style={{ marginTop: '24px' }}>
                                <Title level={5}>Selected Segments ({selectedSegments.length})</Title>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                                    {selectedSegments.map((segment) => {
                                        const isExisting = existingSegments.includes(segment);
                                        return (
                                            <div
                                                key={segment}
                                                style={{
                                                    padding: '6px 12px',
                                                    backgroundColor: isExisting ? '#e6f7ff' : '#f6ffed',
                                                    border: `1px solid ${isExisting ? '#91d5ff' : '#b7eb8f'}`,
                                                    borderRadius: '6px',
                                                    fontSize: '14px',
                                                    position: 'relative'
                                                }}
                                            >
                                                {segment}
                                                {!isExisting && (
                                                    <Text style={{
                                                        fontSize: '10px',
                                                        color: '#52c41a',
                                                        marginLeft: '4px'
                                                    }}>
                                                        (new)
                                                    </Text>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                {selectedSegments.filter(segment => !existingSegments.includes(segment)).length > 0 && (
                                    <Text type="secondary" style={{ display: 'block', marginTop: '8px', fontSize: '12px' }}>
                                        Green tags are new segments that will be added when you save.
                                    </Text>
                                )}
                            </div>
                        )}

                        {!couponSegments?.segments?.length && (
                            <div style={{ marginTop: '32px', padding: '16px', backgroundColor: '#fafafa', borderRadius: '6px', textAlign: 'center' }}>
                                <Text type="secondary">
                                    No segments are currently assigned to this campaign.
                                </Text>
                            </div>
                        )}
                    </div>
                )}
            </Drawer>
        </>
    )
}

export default ManageCouponSegments;